import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, Col, Container, Row, Spinner } from 'react-bootstrap';
import { useGetOrderDetailsQuery } from '../slices/ordersApiSlice';
import { BASE_URL } from '../constants';
import Loader from '../components/Loader';
import Message from '../components/Message';

const statusText = {
  DATA_RECEIVED_WITH_PREFIX_LABEL: 'Verzendgegevens ontvangen',
  PRENOTIFICATION_RECEIVED: 'Verzendmelding ontvangen',
  PICKED_UP: 'Pakket opgehaald',
  PROCESSED: 'Pakket verwerkt',
  IN_TRANSIT: 'Onderweg',
  OUT_FOR_DELIVERY: 'Bezorging gestart',
  DELIVERED: 'Bezorgd',
};

const statusDescription = {
  DATA_RECEIVED_WITH_PREFIX_LABEL: 'DHL heeft de verzendgegevens en het label ontvangen. Het pakket is nog niet fysiek bij DHL ingescand.',
  PRENOTIFICATION_RECEIVED: 'DHL heeft de elektronische aankondiging van deze zending ontvangen.',
  PICKED_UP: 'DHL heeft het pakket in ontvangst genomen.',
  PROCESSED: 'Het pakket is bij DHL verwerkt.',
  IN_TRANSIT: 'Het pakket is onderweg naar de bestemming.',
  OUT_FOR_DELIVERY: 'Het pakket is mee met de bezorger.',
  DELIVERED: 'Het pakket is als bezorgd geregistreerd.',
};

const humanizeStatus = (status) => statusText[status] || (status || 'Status bijgewerkt').replaceAll('_', ' ').toLowerCase();

const ShipmentTrackingScreen = () => {
  const { id: orderId } = useParams();
  const { data: order, isLoading: orderLoading, error: orderError } = useGetOrderDetailsQuery(orderId);
  const [tracking, setTracking] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingError, setTrackingError] = useState('');

  const trackerCode = order?.shippingLabel?.trackingNumber;

  useEffect(() => {
    if (!trackerCode) return undefined;

    const controller = new AbortController();
    setTrackingLoading(true);
    setTrackingError('');

    fetch(`${BASE_URL}/api/dhl/track-trace?key=${encodeURIComponent(trackerCode)}`, {
      credentials: 'include',
      signal: controller.signal,
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok || data.status === 'error') {
          throw new Error(data.errorDetails?.message || data.message || 'De trackinggegevens konden niet worden opgehaald.');
        }
        return data;
      })
      .then(setTracking)
      .catch((error) => {
        if (error.name !== 'AbortError') setTrackingError(error.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setTrackingLoading(false);
      });

    return () => controller.abort();
  }, [trackerCode]);

  const events = useMemo(() => {
    const shipments = Array.isArray(tracking?.dhlResponse)
      ? tracking.dhlResponse
      : tracking?.dhlResponse ? [tracking.dhlResponse] : [];
    return shipments
      .flatMap((shipment) => shipment.events || [])
      .sort((a, b) => new Date(b.timestamp || b.localTimestamp) - new Date(a.timestamp || a.localTimestamp));
  }, [tracking]);

  if (orderLoading) return <Loader />;
  if (orderError) return <Message variant="danger">{orderError?.data?.message || 'De bestelling kon niet worden geladen.'}</Message>;

  if (!trackerCode) {
    return (
      <Container className="py-4">
        <Alert variant="warning">
          <Alert.Heading>Geen track-and-trace beschikbaar</Alert.Heading>
          Voor deze bestelling is nog geen DHL-trackingcode opgeslagen.
        </Alert>
        <Link to={`/order/${orderId}`}><Button variant="outline-secondary">Terug naar bestelling</Button></Link>
      </Container>
    );
  }

  const latestStatus = events[0]?.status;

  return (
    <Container className="py-4">
      <Row className="justify-content-center">
        <Col lg={8}>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div>
              <h1 className="h3 mb-1">Zending volgen</h1>
              <div className="text-muted">Bestelling #{orderId}</div>
            </div>
            <Link to={`/order/${orderId}`}><Button variant="outline-secondary">Terug naar bestelling</Button></Link>
          </div>

          <Card className="shadow-sm mb-4">
            <Card.Body>
              <div className="text-muted small">Track-and-tracecode</div>
              <div className="d-flex align-items-center flex-wrap gap-2">
                <span className="fs-5 fw-semibold">{trackerCode}</span>
                <Badge bg={latestStatus === 'DELIVERED' ? 'success' : 'primary'}>
                  {latestStatus ? humanizeStatus(latestStatus) : 'Nog geen scan'}
                </Badge>
              </div>
              <p className="mb-0 mt-2 text-muted">
                {latestStatus
                  ? statusDescription[latestStatus] || 'De meest recente DHL-verzendgebeurtenis staat hieronder.'
                  : 'DHL heeft nog geen trackinggebeurtenissen voor deze zending gemeld.'}
              </p>
            </Card.Body>
          </Card>

          {trackingLoading && <div className="text-center py-3"><Spinner animation="border" role="status" /><span className="visually-hidden">Tracking ophalen</span></div>}
          {trackingError && <Alert variant="danger">{trackingError}</Alert>}

          {!trackingLoading && !trackingError && (
            <Card className="shadow-sm">
              <Card.Body>
                <h2 className="h5 mb-3">Verzendgeschiedenis</h2>
                {events.length === 0 ? (
                  <p className="text-muted mb-0">Er zijn nog geen updates. Zodra DHL het pakket scant, verschijnt de voortgang hier.</p>
                ) : (
                  <ol className="list-unstyled mb-0">
                    {events.map((event, index) => (
                      <li key={`${event.timestamp || event.localTimestamp}-${event.status}-${index}`} className="d-flex gap-3 pb-3">
                        <span className={`rounded-circle mt-1 ${index === 0 ? 'bg-primary' : 'bg-secondary'}`} style={{ width: 12, height: 12, flex: '0 0 12px' }} />
                        <div>
                          <div className="fw-semibold">{humanizeStatus(event.status)}</div>
                          <div className="text-muted small">
                            {statusDescription[event.status] || event.category?.replaceAll('_', ' ').toLowerCase()}
                          </div>
                          <time className="text-muted small" dateTime={event.localTimestamp || event.timestamp}>
                            {new Date(event.localTimestamp || event.timestamp).toLocaleString('nl-NL')}
                          </time>
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default ShipmentTrackingScreen;
